import { useToast } from 'hooks/useToast';
import { apiClient } from 'api';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface TechnicalSkillsPayload {
  assessment_id: string;
  [key: string]: string;
}

export interface DeleteTechnicalSkillsPayload {
  assessment_id: string;
}

/**
 * Hook for submitting technical skills
 */
export const useTechnicalSkillsEdit = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const patchMutation = useMutation({
    mutationFn: async (payload: TechnicalSkillsPayload) => {
      const response = await apiClient.patch('/assessment/technical-skill/', payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['technicalSkills', assessmentId] });
      openToast({
        show: true,
        heading: "Edit Successful",
        text: "Technical skills edited successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || 'Failed to submit technical skills';
      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    submitTechnicalSkills: patchMutation.mutate,
    isSubmitting: patchMutation.isPending,
  };
};

/**
 * Hook for deleting technical skills
 */
export const useTechnicalSkillsDelete = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async (payload: DeleteTechnicalSkillsPayload) => {
      const response = await apiClient.delete('/assessment/technical-skill/', {
        data: payload,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['technicalSkills', assessmentId] });
      openToast({
        show: true,
        heading: "Delete Successful",
        text: "Technical skills deleted successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || 'Failed to delete technical skills';
      openToast({
        show: true,
        heading: "Delete Failed",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    deleteTechnicalSkills: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};